import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AgentQAModel } from 'models/AgentQAModel';
import { IAgentQACreate, ITranslationBank } from 'types/agentQA';
import DialectSelect, { IDialectOption } from 'components/selects/DialectSelect';
import { SelectLayout } from 'components/selects/SelectLayout';
import { getCacheKey } from 'helpers/cache';
import { IRefIds } from 'constants/refs';
import { useCacheData } from 'hooks/useCacheData';
import CoursesSelectModel from 'models/CoursesSelectModel';
import { ICourse } from 'types/courses';
import BankSelector from './BankSelector';

interface IProps {
  model: AgentQAModel;
  onClose: () => void;
  onSuccess: (jobId: number) => void;
}

interface IAgentType {
  value: string;
  label: string;
  description?: string;
  requiresCourses?: boolean;
  requiresBanks?: boolean;
}

const AgentQACreate: React.FC<IProps> = ({ model, onClose, onSuccess }) => {
  const [name, setName] = useState<string>('');
  const [agentType, setAgentType] = useState<string>('');
  const [dialectId, setDialectId] = useState<number | ''>('');
  const [selectedCourses, setSelectedCourses] = useState<ICourse[]>([]);
  const [selectedBanks, setSelectedBanks] = useState<ITranslationBank[]>([]);
  const [numberOfRuns, setNumberOfRuns] = useState<number>(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentTypes, setAgentTypes] = useState<IAgentType[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  const cacheKey = getCacheKey(IRefIds.courses, 0);
  const dataModel = useMemo(() => new CoursesSelectModel(), []);
  const courseOptions = useCacheData(cacheKey, dataModel.get.bind(dataModel));

  // Fetch agent types from API
  useEffect(() => {
    const fetchAgentTypes = async () => {
      try {
        const response = await model.getAgentTypes();
        if (response?.agent_types) {
          const types = response.agent_types.map((type: any) => ({
            value: type.value,
            label: type.label,
            description: type.description,
            requiresCourses: type.requiresCourses || type.requires_courses || false,
            requiresBanks: type.requiresBanks || type.requires_banks || false,
          }));
          setAgentTypes(types);
          // Set default agent type to first one
          if (types.length > 0) {
            setAgentType(types[0].value);
          }
        }
      } catch (err) {
        console.error('Failed to fetch agent types:', err);
        setError('Failed to load agent types');
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchAgentTypes();
  }, [model]);

  const handleDialectChange = useCallback((dialect: IDialectOption) => {
    setDialectId(dialect.dialectId);
  }, []);

  const handleCourseSelection = useCallback((value: ICourse[] | null) => {
    setSelectedCourses(value ?? []);
  }, []);

  const handleBankSelection = useCallback((value: ITranslationBank[]) => {
    setSelectedBanks(value);
  }, []);

  const selectedAgentType = agentTypes.find((type) => type.value === agentType);
  const needsCourses = selectedAgentType?.requiresCourses || false;
  const needsBanks = selectedAgentType?.requiresBanks || false;

  const handleCreate = async () => {
    setError(null);

    // Validation
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }
    if (!agentType) {
      setError('Please select an agent type');
      return;
    }
    if (!dialectId) {
      setError('Please select a dialect');
      return;
    }
    if (needsCourses && selectedCourses.length === 0) {
      setError('Please select at least one course');
      return;
    }
    if (needsBanks && selectedBanks.length === 0) {
      setError('Please select at least one bank');
      return;
    }

    setSaving(true);
    try {
      const payload: IAgentQACreate = {
        name: name.trim(),
        agent_type: agentType,
        dialect_id: Number(dialectId),
        number_of_runs: numberOfRuns,
        ...(needsCourses && { courses: selectedCourses.map((c) => c.id) }),
        ...(needsBanks && { bank_ids: selectedBanks.map((b) => b.id) }),
      };

      const res = await model.createJob(payload);
      if (res.data?.id && res.data.id > 0) {
        onSuccess(res.data.id);
      } else if (res.data?.message) {
        setError(res.data.message as string);
      } else if ((res as any)?.data?.error) {
        setError((res as any).data.error as string);
      } else {
        setError('Failed to create QA job');
      }
    } catch (e) {
      setError('Failed to create QA job');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Create Agent QA Job</Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>

      <Content>
        <InfoBox>
          <InfoTitle>About Agent QA</InfoTitle>
          <InfoList>
            <li>
              <strong>Purpose:</strong> Test AI agents with validated data to ensure prompt changes don't break existing
              functionality
            </li>
            <li>
              <strong>How it works:</strong> Re-runs the agent on items with known correct results and compares output
            </li>
            <li>
              <strong>Results:</strong> Shows pass/fail status with reasoning for failures
            </li>
          </InfoList>
        </InfoBox>

        <Section>
          <SectionTitle>Name *</SectionTitle>
          <NameInput
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Test PT-BR prompt changes"
            autoFocus
          />
        </Section>

        <Section>
          <SectionTitle>Agent Type *</SectionTitle>
          <Select value={agentType} onChange={(e) => setAgentType(e.target.value)} disabled={loadingTypes}>
            {loadingTypes ? (
              <option value="">Loading agent types...</option>
            ) : agentTypes.length === 0 ? (
              <option value="">No agent types available</option>
            ) : (
              agentTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))
            )}
          </Select>
          {selectedAgentType?.description && (
            <HelpText>{selectedAgentType.description}</HelpText>
          )}
        </Section>

        {needsBanks && (
          <BankSelector
            agentType={agentType}
            selectedBanks={selectedBanks}
            onChange={handleBankSelection}
            model={model}
          />
        )}

        {needsCourses && (
          <Section>
            <SectionTitle>Courses *</SectionTitle>
            <SelectLayout
              data={courseOptions}
              onChange={handleCourseSelection}
              label="Select courses"
              optionLabelKey="name"
              optionValueKey="id"
              currentValue={selectedCourses}
              multiple
            />
            <HelpText>Select one or more courses to test with this agent.</HelpText>
          </Section>
        )}

        <Section>
          <SectionTitle>Dialect *</SectionTitle>
          <DialectSelect handleDialectChange={handleDialectChange} onlySource />
        </Section>

        <Section>
          <SectionTitle>Number of Runs</SectionTitle>
          <NumberInput
            type="number"
            min={1}
            max={10}
            value={numberOfRuns}
            onChange={(e) => setNumberOfRuns(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
          />
          <HelpText>Run each test item multiple times (1-10) to assess AI consistency.</HelpText>
        </Section>

        {error && <ErrorText>{error}</ErrorText>}
      </Content>

      <Footer>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <CreateButton onClick={handleCreate} disabled={saving}>
          {saving ? 'Creating...' : 'Create QA Job'}
        </CreateButton>
      </Footer>
    </Container>
  );
};

export default AgentQACreate;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 30px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const InfoBox = styled.div`
  border-left: 4px solid #2196f3;
  background: #e3f2fd;
  color: #1565c0;
  padding: 12px 14px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 15px;
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: 20px;
  li {
    line-height: 1.5;
    margin-bottom: 6px;
    font-size: 14px;
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 16px;
  color: #333;
`;

const NameInput = styled.input`
  width: 94%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }
`;

const NumberInput = styled.input`
  width: 80px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }
`;

const Select = styled.select`
  width: 97%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background-color: #ffebee;
  border-radius: 4px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #e0e0e0;
  }
`;

const CreateButton = styled.button`
  padding: 10px 20px;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    background-color: #1976d2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 8px;
  font-style: italic;
`;

